import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ChevronLeft, ChevronRight, Check, Mail, Loader2, FileCheck, Sparkles, ShieldCheck } from 'lucide-react';
import { PROVIDER_ROLES, POH_SPECIALIZATIONS, REPORT_VERBOSITY_OPTIONS } from '@/types/practice-settings';
import { ApiFout, api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import isoftLogo from '@/assets/isoft-logo.png';

interface OnboardingWizardProps {
  onComplete: (settings: OnboardingSettings) => void;
  userName?: string;
  /**
   * True zolang de praktijknaam nog niet is ingevuld; dan vraagt de wizard erom en
   * toont hij het welkomstscherm en de uitleg over de verwerkersovereenkomst.
   *
   * Dit hing aan practiceIsNew uit de tokenruil. Die is alleen waar bij het
   * verzoek dat de praktijk aanmaakte, dus na een herlaadactie halverwege de wizard
   * verdween de vraag om de naam en bleef "Nieuwe praktijk" in de database staan --
   * met die naam in het addendum bij de verificatiemail.
   */
  practiceNeedsName?: boolean;
  practiceUserId?: string;
  /**
   * token en userId zijn hier weg. Die werden meegegeven omdat
   * send-verification-email ze in zijn body verwachtte; de sessie zit nu in een
   * HttpOnly-cookie en de routes onder /api/me weten wie de aanvrager is.
   */
}

export interface OnboardingSettings {
  display_name: string;
  practice_name?: string;
  provider_role: string;
  provider_specializations: string[];
  report_verbosity: string;
  doctor_phrase: string;
  read_trigger_phrase: string;
  personal_profile: string;
  email?: string;
}

const ALL_STEPS = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const NAMED_PRACTICE_SKIP = [0, 7]; // welkom & verwerkersovereenkomst: alleen bij het inrichten van de praktijk

export function OnboardingWizard({ onComplete, userName, practiceNeedsName, practiceUserId }: OnboardingWizardProps) {
  const steps = ALL_STEPS.filter(s => practiceNeedsName || !NAMED_PRACTICE_SKIP.includes(s));
  const totalSteps = steps.length;

  const [step, setStep] = useState(steps[0]);
  const stepIndex = steps.indexOf(step);
  const [displayName, setDisplayName] = useState(userName || '');
  const [practiceName, setPracticeName] = useState('');
  const [providerRole, setProviderRole] = useState('');
  const [providerSpecializations, setProviderSpecializations] = useState<string[]>([]);
  const [reportVerbosity, setReportVerbosity] = useState('gemiddeld');
  const [doctorPhrase, setDoctorPhrase] = useState('');
  const [readTriggerPhrase, setReadTriggerPhrase] = useState('');
  const [personalProfile, setPersonalProfile] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  /**
   * De praktijknaam zoals die in het verstuurde addendum staat, of null zolang er
   * geen mail uit is. Wijzigt de naam daarna nog, dan is het verstuurde document
   * niet meer het document dat de praktijk aanvaardt en moet de code opnieuw.
   */
  const [naamInAddendum, setNaamInAddendum] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const canProceed = () => {
    switch (step) {
      case 0: return isAuthorized;
      case 1: return displayName.trim().length > 0 && (!practiceNeedsName || practiceName.trim().length > 0);
      case 2: return providerRole.length > 0;
      case 8: return emailVerified;
      default: return true;
    }
  };

  const handleSendCode = async () => {
    if (!email.includes('@') || !practiceUserId) return;
    setIsSending(true);
    try {
      // Eerst de praktijknaam vastleggen, dan de mail aanvragen. De bijlage bij die
      // mail is het addendum verwerkersovereenkomst, en de API vult daar de naam in
      // die op dat moment in de database staat (routes/email.ts). De naam uit stap 1
      // ging pas bij "Voltooien" naar de database -- ná deze mail -- dus stond er
      // "Nieuwe praktijk" in het exemplaar dat de praktijk met de code aanvaardt.
      //
      // Mislukt het opslaan, dan gaat de mail niet: een addendum op de verkeerde
      // naam is erger dan een code die nog moet komen.
      if (practiceNeedsName && practiceName.trim()) {
        await api.patch('/practice', { practiceName: practiceName.trim() });
      }

      // De practice_user_id gaat niet meer mee: de route leest hem uit de sessie. In
      // de bron stond hij in de body, en dan bepaalt de client voor wie de code geldt.
      await api.post('/me/email/verify-request', { email });
      setCodeSent(true);
      setNaamInAddendum(practiceName.trim());
      toast({ title: 'Code verzonden', description: `Verificatiecode is verstuurd naar ${email}` });
    } catch (fout) {
      toast({
        title: 'Fout',
        description:
          fout instanceof ApiFout ? fout.message : 'Kon verificatie-e-mail niet verzenden',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  /**
   * De praktijknaam wijzigen. Staat er al een addendum met een andere naam bij de
   * praktijk in de mailbox, dan vervalt die verificatie: het document dat met de
   * code wordt aanvaard hoort de naam te dragen die de gebruiker uiteindelijk
   * opgeeft. Dezelfde regel als bij het e-mailadres hieronder.
   */
  const wijzigPraktijknaam = (waarde: string) => {
    setPracticeName(waarde);

    if (naamInAddendum !== null && waarde.trim() !== naamInAddendum) {
      setCodeSent(false);
      setEmailVerified(false);
      setVerificationCode('');
      setNaamInAddendum(null);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || !practiceUserId) return;
    setIsVerifying(true);
    try {
      await api.post('/me/email/verify', { code: verificationCode.trim() });
      setEmailVerified(true);
      toast({ title: 'Geverifieerd', description: 'Uw e-mailadres is succesvol geverifieerd.' });
    } catch (fout) {
      // De route zegt hoeveel pogingen er nog over zijn; die melding is nuttiger dan
      // een eigen tekst.
      toast({
        title: 'Fout',
        description: fout instanceof ApiFout ? fout.message : 'Ongeldige code',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleNext = () => {
    const nextIdx = stepIndex + 1;
    if (nextIdx < totalSteps) {
      setStep(steps[nextIdx]);
    } else {
      onComplete({
        display_name: displayName.trim(),
        practice_name: practiceNeedsName ? practiceName.trim() : undefined,
        provider_role: providerRole,
        provider_specializations: providerRole === 'poh' ? providerSpecializations : [],
        report_verbosity: reportVerbosity,
        doctor_phrase: doctorPhrase.trim(),
        read_trigger_phrase: readTriggerPhrase.trim(),
        personal_profile: personalProfile.trim(),
        email: email.trim(),
      });
    }
  };

  const handleBack = () => {
    const prevIdx = stepIndex - 1;
    if (prevIdx >= 0) setStep(steps[prevIdx]);
  };

  const toggleSpecialization = (value: string) => {
    setProviderSpecializations(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  return (
    <div className="h-screen flex items-center justify-center bg-background p-2">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold">{step === 0 ? 'Welkom' : 'Instellen profiel'}</span>
          <span className="text-xs text-muted-foreground">Stap {stepIndex + 1}/{totalSteps}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5 mb-3">
          <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }} />
        </div>

        {/* Content */}
        <div className="space-y-3">
          {step === 0 && (
            <div className="space-y-3">
              <div className="flex flex-col items-center gap-2">
                <img src={isoftLogo} alt="iSOFT logo" width={120} height={120} className="mb-1" />
                <h2 className="text-base font-semibold">Welkom bij iSOFT Voice-to-SOEP</h2>
              </div>
              <div className="bg-muted/50 rounded-md p-3 space-y-2 text-sm text-muted-foreground">
                <p>
                  Deze app zet uw <strong>gesproken consultatie</strong> automatisch om naar een volledig 
                  <strong> SOEP-verslag</strong> met AI. Daarnaast kunt u de app gebruiken als <strong>vertaler</strong> — 
                  spreek in het Nederlands en laat de vertaling direct voorlezen aan de patiënt.
                </p>
                <p>
                  Er is <strong>geen abonnement</strong> nodig. U betaalt alleen voor daadwerkelijk gebruik (<em>pay-per-use</em>). 
                  De kosten zijn volledig <strong>transparant en inzichtelijk</strong> via uw persoonlijke kostenoverzicht.
                </p>
                <p>
                  U zit nergens aan vast — elke eerste week na het verstrijken van de maand ontvangt u een factuur 
                  voor het gebruik van de afgelopen maand.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                In de volgende stappen stellen we uw profiel in. Dit duurt ongeveer 2 minuten.
              </p>
              <div className="flex items-center justify-between gap-3 p-3 rounded-md border border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                  <Label htmlFor="authorized-toggle" className="text-xs cursor-pointer">
                    Ik ben bevoegd om namens deze praktijk deze overeenkomst aan te gaan (tekenbevoegd)
                  </Label>
                </div>
                <Switch id="authorized-toggle" checked={isAuthorized} onCheckedChange={setIsAuthorized} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {practiceNeedsName && (
                <div className="space-y-1">
                  <Label htmlFor="practice_name" className="text-xs">Praktijknaam</Label>
                  <p className="text-xs text-muted-foreground">Nieuwe praktijk — vul de naam in.</p>
                  <Input id="practice_name" value={practiceName} onChange={e => wijzigPraktijknaam(e.target.value)} placeholder="bijv. Huisartsenpraktijk De Linde" autoFocus className="h-8 text-sm" />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="display_name" className="text-xs">Uw naam</Label>
                <p className="text-xs text-muted-foreground">Zichtbaar voor collega's binnen uw praktijk.</p>
                <Input id="display_name" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="bijv. Dr. Jansen" autoFocus={!practiceNeedsName} className="h-8 text-sm" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Uw rol</Label>
                <p className="text-xs text-muted-foreground">Bepaalt de stijl van de AI-verslaglegging.</p>
              </div>
              <RadioGroup value={providerRole} onValueChange={setProviderRole} className="space-y-1">
                {PROVIDER_ROLES.map(role => (
                  <div key={role.value} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-muted">
                    <RadioGroupItem value={role.value} id={`role-${role.value}`} />
                    <Label htmlFor={`role-${role.value}`} className="cursor-pointer text-sm">{role.label}</Label>
                  </div>
                ))}
              </RadioGroup>
              {providerRole === 'poh' && (
                <div className="space-y-1 pt-1 border-t">
                  <p className="text-xs text-muted-foreground">Selecteer specialisatie(s):</p>
                  <div className="grid grid-cols-2 gap-1">
                    {POH_SPECIALIZATIONS.map(spec => (
                      <div key={spec.value} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-muted">
                        <Checkbox id={`spec-${spec.value}`} checked={providerSpecializations.includes(spec.value)} onCheckedChange={() => toggleSpecialization(spec.value)} />
                        <Label htmlFor={`spec-${spec.value}`} className="cursor-pointer text-sm">{spec.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Uitgebreidheid verslaglegging</Label>
                <p className="text-xs text-muted-foreground">Hoe gedetailleerd moeten uw SOEP-verslagen zijn?</p>
              </div>
              <RadioGroup value={reportVerbosity} onValueChange={setReportVerbosity} className="space-y-1">
                {REPORT_VERBOSITY_OPTIONS.map(opt => (
                  <div key={opt.value} className="flex items-center space-x-2 py-1.5 px-2 rounded border hover:bg-muted">
                    <RadioGroupItem value={opt.value} id={`verb-${opt.value}`} />
                    <Label htmlFor={`verb-${opt.value}`} className="cursor-pointer text-sm flex-1">
                      <span className="font-medium">{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {opt.value === 'beknopt' && 'Kort en bondig'}
                        {opt.value === 'gemiddeld' && 'Standaard balans'}
                        {opt.value === 'uitgebreid' && 'Meer context en detail'}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-2">
              <div>
                <Label htmlFor="doctor_phrase" className="text-xs">Herkenningszin(nen)</Label>
                <p className="text-xs text-muted-foreground">
                  Zin die u uitspreekt zodat het systeem weet wie de arts is. Meerdere zinnen scheiden met komma.
                  <br /><strong>Let op:</strong> herkenning is exact — voeg varianten toe (bijv. <em>voor u doen, voor je doen</em>).
                </p>
              </div>
              <Input id="doctor_phrase" value={doctorPhrase} onChange={e => setDoctorPhrase(e.target.value)} placeholder="bijv. Wat kan ik voor u doen, Wat kan ik voor je doen" autoFocus className="h-8 text-sm" />
            </div>
          )}

          {step === 5 && (
            <div className="space-y-2">
              <div>
                <Label htmlFor="read_trigger" className="text-xs">Voorlees-triggerzin</Label>
                <p className="text-xs text-muted-foreground">
                  Zeg deze zin om automatisch de vertaling voor te lezen aan de patiënt. Leeg laten = uitgeschakeld.
                </p>
              </div>
              <Input id="read_trigger" value={readTriggerPhrase} onChange={e => setReadTriggerPhrase(e.target.value)} placeholder="bijv. Stop, Vertaal" autoFocus className="h-8 text-sm" />
            </div>
          )}

          {step === 6 && (
            <div className="space-y-2">
              <div>
                <Label htmlFor="personal_profile" className="text-xs">Persoonlijk profiel (optioneel)</Label>
                <p className="text-xs text-muted-foreground">
                  Aanvullende AI-instructies: terminologie, aandachtspunten, werkwijze. 
                  Ook de <strong>persoon</strong> van verslaglegging: bijv. <em>"Schrijf in de 3e persoon"</em> of <em>"Schrijf in de 1e persoon vanuit patiënt"</em>.
                </p>
              </div>
              <Textarea id="personal_profile" value={personalProfile} onChange={e => setPersonalProfile(e.target.value)} placeholder="bijv. Schrijf in de 3e persoon. Gebruik NHG-standaard terminologie." rows={3} autoFocus className="text-sm" />
            </div>
          )}

          {step === 7 && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <FileCheck className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-semibold">Verwerkersovereenkomst</Label>
                </div>
              </div>
              <div className="bg-muted/50 rounded-md p-3 space-y-2 text-sm text-muted-foreground">
                <p>
                  In de volgende stap wordt u gevraagd uw <strong>zakelijke e-mailadres te verifiëren</strong>.
                </p>
                <p>
                  Door uw e-mailadres te verifiëren gaat u akkoord met de <strong>Verwerkersovereenkomst AI-diensten</strong> 
                  (Addendum conform het BoZ-model) die van toepassing is op het gebruik van de spraak- en AI-functionaliteiten 
                  binnen dit systeem.
                </p>
                <p>
                  De volledige verwerkersovereenkomst wordt als <strong>PDF-bijlage</strong> bij de verificatie-email meegezonden, 
                  zodat u deze kunt bewaren voor uw administratie.
                </p>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-md p-3 text-xs">
                <p className="font-medium text-foreground mb-1">Samengevat:</p>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  <li>Spraakdata wordt uitsluitend tijdelijk en in-memory verwerkt (zero retention)</li>
                  <li>Alle verwerking vindt plaats binnen de EU (Amsterdam & Zweden)</li>
                  <li>Er worden geen medische gegevens permanent opgeslagen door de AI-dienst</li>
                </ul>
              </div>
            </div>
          )}

          {step === 8 && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Mail className="h-4 w-4 text-primary" />
                  <Label className="text-xs">E-mailverificatie</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Vul uw zakelijke e-mailadres in. U ontvangt een 6-cijferige code ter verificatie.
                  De verwerkersovereenkomst is als PDF bijgesloten bij deze email.
                </p>
              </div>

              <div className="space-y-2">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setCodeSent(false); setEmailVerified(false); }}
                  placeholder="bijv. j.jansen@huisarts.nl"
                  className="h-8 text-sm"
                  disabled={emailVerified}
                  autoFocus
                />
                {!emailVerified && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleSendCode}
                    disabled={!email.includes('@') || isSending}
                  >
                    {isSending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                    {codeSent ? 'Opnieuw verzenden' : 'Verificatiecode verzenden'}
                  </Button>
                )}
              </div>

              {codeSent && !emailVerified && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">Voer de 6-cijferige code in die u per e-mail heeft ontvangen:</p>
                  {/* Zonder deze regel wordt "Opnieuw verzenden" na een paar seconden
                      aangeklikt, en dat maakt de eerste code juist ongeldig: elke
                      aanvraag verwijdert de vorige -- zie routes/email.ts. */}
                  <p className="text-xs text-muted-foreground">
                    Het kan tot een minuut duren voordat de e-mail binnenkomt. Controleer ook uw
                    map met ongewenste e-mail.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="h-8 text-sm text-center tracking-widest font-mono"
                      maxLength={6}
                    />
                    <Button
                      size="sm"
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6 || isVerifying}
                    >
                      {isVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              )}

              {emailVerified && (
                <div className="flex items-center gap-2 p-2 rounded bg-primary/10 text-primary text-sm">
                  <Check className="h-4 w-4" />
                  <span>E-mailadres geverifieerd</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-4 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={handleBack} disabled={stepIndex === 0}>
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            Vorige
          </Button>
          <Button size="sm" onClick={handleNext} disabled={!canProceed()}>
            {stepIndex === totalSteps - 1 ? (
              <><Check className="h-3.5 w-3.5 mr-1" />Voltooien</>
            ) : (
              <>{step === 0 ? 'Aan de slag' : 'Volgende'}<ChevronRight className="h-3.5 w-3.5 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
